import { Box } from '@mui/material';
import usePageTitle from 'hooks/usePageTitle';
import { InfoTooltip, Tabs } from 'DesignSystem';
import { ITab } from 'DesignSystem/Primitives/Tabs/Tabs';
import React, { FC, PropsWithChildren } from 'react';
import { useNavigate } from 'react-router-dom';
import { EAvailableSizes } from 'DesignSystem/types/size.types';
import { useCompanyPlanContext } from 'FeatureModules/Auth/Contexts/CompanyPlanContext';
import { ECustomerPlan } from 'FeatureModules/Auth/types/company.types';
import PageContainer from 'Components/PageContainer';
import PageTitle from 'Components/PageTitle';

enum EComponentChainTabKey {
  COMPONENTS = 'components',
  CHAINS = 'component-chains',
}

interface IComponentsLayoutProps extends PropsWithChildren {
  selectedTab: `${EComponentChainTabKey}`;
}

const COMPONENT_TABS: ITab<EComponentChainTabKey>[] = [
  {
    key: EComponentChainTabKey.CHAINS,
    label: 'Component chains',
    startIconName: 'component-chain',
  },
  {
    key: EComponentChainTabKey.COMPONENTS,
    label: 'Unique components',
    startIconName: 'components',
  },
];

const ComponentsLayout: FC<IComponentsLayoutProps> = ({ children, selectedTab }) => {
  usePageTitle('Unique Components');
  const { plan } = useCompanyPlanContext();
  const isPartner = plan === ECustomerPlan.FREEMIUM;

  const navigate = useNavigate();

  const handleChangeTab = (tab: EComponentChainTabKey) => {
    if (tab === EComponentChainTabKey.COMPONENTS) {
      navigate(`/components`);
    } else {
      navigate(`/component-chains`);
    }
  };

  return (
    <PageContainer>
      <Box display="flex" alignItems="center">
        <PageTitle
          title="Component chains"
          TitleAdornment={
            <Box ml={1}>
              <InfoTooltip
                size={EAvailableSizes.LARGE}
                text={`Components are unique ingredients, packaging or something else that makes up your end products. All components that are part of your chains appear here. 
                  You can specify what components are coming from what partner and re-use your components in several chain activities. Component chains are re-usable sub-supply chains. 
                  They describe the material flow of your components${
                    isPartner
                      ? '.'
                      : ', and can also be re-used across several of your different product’s supply chains.'
                  }`}
              />
            </Box>
          }
        />
      </Box>

      <Box mt={3} mb={1}>
        <Tabs
          tabs={COMPONENT_TABS}
          selectedTab={selectedTab as EComponentChainTabKey}
          onChange={handleChangeTab}
        />
      </Box>

      {children}
    </PageContainer>
  );
};

export default ComponentsLayout;
